// GiftCardReport.jsx (FIXED + FINAL)
// ✅ Matches API shape: "Branch Name", "Number Of GiftCard", "Total Amount", "Claim Amount", "UnClaim Amount"
// ✅ Robust pagination: uses server pagination if provided, otherwise falls back to client-side pagination
// ✅ Sorting + totals + CSV export updated for gift-card report

import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import '../report.css';
import ApiService from '../../../../services/apiService';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
// ---------------- HELPERS ----------------
const numberFmt = (n, digits = 2) =>
  new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(+n) ? +n : 0);

// For <input type="date" />
const toDateInputValue = (d) => {
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const COLUMNS = [
  { key: 'branchName', label: 'Branch Name' },
  { key: 'giftCardCount', label: 'Number Of GiftCard', align: 'right' },
  { key: 'totalAmount', label: 'Total Amount', align: 'right' },
  { key: 'claimAmount', label: 'Claim Amount', align: 'right' },
  { key: 'unclaimAmount', label: 'UnClaim Amount', align: 'right' },
];

// ---------------- COMPONENT ----------------
const GiftCardReport = ({ Currency }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [serverPaging, setServerPaging] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalRecords: 0,
    totalPages: 1,
    hasPrevious: false,
    hasNext: false,
  });

  // Default last 10 days
  const now = new Date();
  const tenDaysAgo = new Date(now);
  tenDaysAgo.setDate(now.getDate() - 10);

  const [formData, setFormData] = useState({
    fromDate: toDateInputValue(tenDaysAgo),
    toDate: toDateInputValue(now),
  });

  // ---------------- Fetch Branches ----------------
  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');

      const list = res?.data?.result || [];
      const mapped = list.map((b) => ({
        value: b.id,
        label: b.branchName,
      }));

      setBranches(mapped);
    } catch (e) {
      console.error('Failed to fetch branches', e);
    }
  };

  // ---------------- Fetch Report Data ----------------
  const normalizeGiftCardRow = (r) => ({
    branchName: r?.['Branch Name'] ?? r?.branchName ?? '—',
    giftCardCount: +(r?.['Number Of GiftCard'] ?? r?.numberOfGiftCard ?? r?.giftCardCount ?? 0),
    totalAmount: +(r?.['Total Amount'] ?? r?.totalAmount ?? 0),
    claimAmount: +(r?.['Claim Amount'] ?? r?.claimAmount ?? 0),
    unclaimAmount: +(r?.['UnClaim Amount'] ?? r?.unClaimAmount ?? r?.unclaimAmount ?? 0),
  });

  const detectServerPaging = (apiPg) => {
    if (!apiPg) return false;

    const hasKnownTotals =
      (Number.isFinite(+apiPg.totalRecords) && apiPg.totalRecords !== null) || (Number.isFinite(+apiPg.totalPages) && apiPg.totalPages !== null);

    const hasExplicitFlags = typeof apiPg.hasPrevious === 'boolean' || typeof apiPg.hasNext === 'boolean';

    return Boolean(hasKnownTotals || hasExplicitFlags);
  };

  const fetchData = async (page = 1, size = pagination.pageSize) => {
    setLoading(true);
    setErr('');

    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      // Keep existing pagination params (server may or may not honor them)
      params.append('PageNo', page);
      params.append('PageSize', size);

      // Your report params
      params.append('ReportType', 'GiftCard Claim & UnClaimed');
      params.append('StartDate', formData.fromDate);
      params.append('EndDate', formData.toDate);
      params.append('Comparison', 'No Comparison');

      // Always send BranchId → 0 = all branches
      params.append('BranchId', selectedBranch?.value || 0);

      const res = await apiService.getApiParamater('GET_SALE_SUMMERY', `?${params.toString()}`);

      const result = Array.isArray(res?.data?.result) ? res.data.result : [];
      const apiPg = res?.data?.paginationData || null;

      const normalized = result.map(normalizeGiftCardRow);
      setRows(normalized);

      const isServer = detectServerPaging(apiPg);
      setServerPaging(isServer);

      if (isServer) {
        const totalRecords = Number.isFinite(+apiPg.totalRecords) && +apiPg.totalRecords >= 0 ? +apiPg.totalRecords : normalized.length;

        const totalPages = Number.isFinite(+apiPg.totalPages) && +apiPg.totalPages > 0 ? +apiPg.totalPages : Math.max(1, Math.ceil(totalRecords / size));

        const hasPrevious = typeof apiPg.hasPrevious === 'boolean' ? apiPg.hasPrevious : page > 1;
        const hasNext = typeof apiPg.hasNext === 'boolean' ? apiPg.hasNext : page < totalPages;

        setPagination((p) => ({
          ...p,
          currentPage: page,
          pageSize: size,
          totalRecords,
          totalPages,
          hasPrevious,
          hasNext,
        }));
      } else {
        // Client-side pagination fallback
        const totalRecords = normalized.length;
        const totalPages = Math.max(1, Math.ceil(totalRecords / size));

        setPagination((p) => ({
          ...p,
          currentPage: page,
          pageSize: size,
          totalRecords,
          totalPages,
          hasPrevious: page > 1,
          hasNext: page < totalPages,
        }));
      }
    } catch (e) {
      console.error(e);
      setErr('Failed to fetch report. Please try again.');
      setRows([]);
      setServerPaging(false);
      setPagination((p) => ({
        ...p,
        currentPage: 1,
        totalRecords: 0,
        totalPages: 1,
        hasPrevious: false,
        hasNext: false,
      }));
    } finally {
      setLoading(false);
    }
  };

  // Load branches once
  useEffect(() => {
    fetchBranches();
  }, []);

  // ---------------- Sorting ----------------
  const [sortState, setSortState] = useState({ key: 'branchName', dir: 'asc' });

  const sortedRows = useMemo(() => {
    const { key, dir } = sortState;
    const copy = [...rows];

    copy.sort((a, b) => {
      const av = a[key];
      const bv = b[key];

      // numeric
      if (typeof av === 'number' && typeof bv === 'number') {
        return dir === 'asc' ? av - bv : bv - av;
      }

      // string
      return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

    return copy;
  }, [rows, sortState]);

  const visibleRows = useMemo(() => {
    if (serverPaging) return sortedRows; // already paged by server
    const start = (pagination.currentPage - 1) * pagination.pageSize;
    return sortedRows.slice(start, start + pagination.pageSize);
  }, [sortedRows, serverPaging, pagination.currentPage, pagination.pageSize]);

  // Totals (based on currently loaded dataset)
  const totals = useMemo(
    () => ({
      giftCards: sortedRows.reduce((s, r) => s + (r.giftCardCount || 0), 0),
      total: sortedRows.reduce((s, r) => s + (r.totalAmount || 0), 0),
      claim: sortedRows.reduce((s, r) => s + (r.claimAmount || 0), 0),
      unclaim: sortedRows.reduce((s, r) => s + (r.unclaimAmount || 0), 0),
    }),
    [sortedRows],
  );

  // ---------------- Handlers ----------------
  const onChangeRange = (e) => setFormData((old) => ({ ...old, [e.target.name]: e.target.value }));

  const onSearch = () => fetchData(1, pagination.pageSize);

  const onSort = (key) => setSortState((prev) => (prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));

  const onPrevPage = () => {
    if (loading || !pagination.hasPrevious) return;

    if (serverPaging) {
      fetchData(pagination.currentPage - 1, pagination.pageSize);
    } else {
      setPagination((p) => {
        const newPage = Math.max(1, p.currentPage - 1);
        return { ...p, currentPage: newPage, hasPrevious: newPage > 1, hasNext: newPage < p.totalPages };
      });
    }
  };

  const onNextPage = () => {
    if (loading || !pagination.hasNext) return;

    if (serverPaging) {
      fetchData(pagination.currentPage + 1, pagination.pageSize);
    } else {
      setPagination((p) => {
        const newPage = Math.min(p.totalPages, p.currentPage + 1);
        return { ...p, currentPage: newPage, hasPrevious: newPage > 1, hasNext: newPage < p.totalPages };
      });
    }
  };

  const onPageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10) || 20;

    if (serverPaging) {
      fetchData(1, newSize);
    } else {
      setPagination((p) => {
        const totalPages = Math.max(1, Math.ceil((p.totalRecords || sortedRows.length) / newSize));
        return {
          ...p,
          pageSize: newSize,
          currentPage: 1,
          totalPages,
          hasPrevious: false,
          hasNext: totalPages > 1,
        };
      });
    }
  };

  // CSV Export (exports ALL loaded rows; if serverPaging is true, it exports current page)
  const exportCSV = () => {
    const exportRows = serverPaging ? sortedRows : sortedRows; // same dataset we have in memory

    if (!exportRows.length) return;

    const headers = COLUMNS.map((c) => `"${c.label}"`).join(',');
    const body = exportRows
      .map((r) =>
        COLUMNS.map((c) => {
          const val = r[c.key];
          return `"${String(val ?? '').replace(/"/g, '""')}"`;
        }).join(','),
      )
      .join('\n');

    const csv = `${headers}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `giftcard-claim-unclaim-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };
  const exportExcel = () => {
    // Same dataset you can export (what you have in memory)
    // If serverPaging=true, rows/sortedRows are usually the current page only.
    const exportRows = sortedRows;

    if (!exportRows.length) return;

    const header = COLUMNS.map((c) => c.label);

    const body = exportRows.map((r) =>
      COLUMNS.map((c) => {
        const v = r?.[c.key];

        // keep numeric columns numeric
        if (c.key === 'giftCardCount') return Number(v) || 0;
        if (c.key === 'totalAmount') return Number(v) || 0;
        if (c.key === 'claimAmount') return Number(v) || 0;
        if (c.key === 'unclaimAmount') return Number(v) || 0;

        return String(v ?? '').trim();
      }),
    );

    const totalsRow = ['Total', Number(totals.giftCards) || 0, Number(totals.total) || 0, Number(totals.claim) || 0, Number(totals.unclaim) || 0];

    const aoa = [header, ...body, totalsRow];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Optional widths
    ws['!cols'] = [
      { wch: 26 }, // Branch Name
      { wch: 18 }, // Number Of GiftCard
      { wch: 16 }, // Total Amount
      { wch: 16 }, // Claim Amount
      { wch: 16 }, // UnClaim Amount
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'GiftCard Report');

    const branchName = selectedBranch?.label ? selectedBranch.label.replace(/[\\/:*?"<>|]/g, '-') : 'All-Branches';
    const date = new Date().toISOString().slice(0, 10);

    XLSX.writeFile(wb, `giftcard-claim-unclaim_${branchName}_${formData.fromDate}_to_${formData.toDate}_${date}.xlsx`);
  };
  const showingFrom =
    pagination.totalRecords === 0
      ? 0
      : serverPaging
        ? (pagination.currentPage - 1) * pagination.pageSize + 1
        : (pagination.currentPage - 1) * pagination.pageSize + 1;

  const showingTo =
    pagination.totalRecords === 0
      ? 0
      : serverPaging
        ? Math.min(pagination.currentPage * pagination.pageSize, pagination.totalRecords)
        : Math.min(pagination.currentPage * pagination.pageSize, pagination.totalRecords);

  return (
    <div className="p-5">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* Filters */}
      <div className="row g-3 align-items-end">
        <div className="col-md-3">
          <label className="form-label">{t('Select Branch') || 'Select Branch'}</label>
          <Select
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            value={selectedBranch}
            onChange={(opt) => setSelectedBranch(opt)}
            options={branches}
            placeholder="Select Branch"
            isClearable
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">{t('From Date') || 'From Date'}</label>
          <input type="date" className="form-control" name="fromDate" value={formData.fromDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-3">
          <label className="form-label">{t('To Date') || 'To Date'}</label>
          <input type="date" className="form-control" name="toDate" value={formData.toDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-3 d-flex gap-3 align-items-end">
          <button className="addItem-btn" onClick={onSearch} disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </button>

          <button className="btn btn-outline-secondary" onClick={exportCSV} disabled={!sortedRows.length}>
            Export CSV
          </button>

          <button className="btn btn-outline-success" onClick={exportExcel} disabled={!sortedRows.length}>
            Export Excel
          </button>
        </div>
      </div>

      {/* Top Summary */}
      {/* {sortedRows.length > 0 && (
        // <div className="mt-3 p-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}>
        //   <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
        //     <div>
        //       <div style={{ fontWeight: 700, color: '#495057' }}>Gift Card Claim & Unclaimed Summary</div>
        //       <div className="text-muted" style={{ fontSize: 13 }}>
        //         Period: <b>{formData.fromDate}</b> to <b>{formData.toDate}</b>
        //         {selectedBranch?.label ? (
        //           <>
        //             {' '}
        //             | Branch: <b>{selectedBranch.label}</b>
        //           </>
        //         ) : (
        //           <>
        //             {' '}
        //             | Branch: <b>All</b>
        //           </>
        //         )}
        //       </div>
        //     </div>

        //     <div className="d-flex flex-wrap gap-3">
        //       <div>
        //         <div className="text-muted" style={{ fontSize: 12 }}>
        //           Gift Cards
        //         </div>
        //         <div style={{ fontWeight: 800 }}>{totals.giftCards}</div>
        //       </div>
        //       <div>
        //         <div className="text-muted" style={{ fontSize: 12 }}>
        //           Total Amount
        //         </div>
        //         <div style={{ fontWeight: 800 }}>
        //           {Currency?.currency?.code} {numberFmt(totals.total)}
        //         </div>
        //       </div>
        //       <div>
        //         <div className="text-muted" style={{ fontSize: 12 }}>
        //           Claim Amount
        //         </div>
        //         <div style={{ fontWeight: 800 }}>
        //           {Currency?.currency?.code} {numberFmt(totals.claim)}
        //         </div>
        //       </div>
        //       <div>
        //         <div className="text-muted" style={{ fontSize: 12 }}>
        //           UnClaim Amount
        //         </div>
        //         <div style={{ fontWeight: 800, color: totals.unclaim < 0 ? '#dc3545' : undefined }}>
        //           {Currency?.currency?.code} {numberFmt(totals.unclaim)}
        //         </div>
        //       </div>
        //     </div>
        //   </div>
        // </div>
      )} */}

      {/* Pagination controls (top) */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 gap-2">
        <div className="text-muted">
          Showing <b>{showingFrom}</b>–<b>{showingTo}</b> of <b>{pagination.totalRecords}</b>
        </div>

        <div className="d-flex align-items-center gap-2">
          <label className="text-muted m-0">Page size</label>
          <select className="form-select form-select-sm" style={{ width: 90 }} value={pagination.pageSize} onChange={onPageSizeChange}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <button className="btn btn-sm btn-outline-secondary" onClick={onPrevPage} disabled={loading || !pagination.hasPrevious}>
            Prev
          </button>

          <span className="text-muted">
            Page <b>{pagination.currentPage}</b> / <b>{pagination.totalPages}</b>
          </span>

          <button className="btn btn-sm btn-outline-secondary" onClick={onNextPage} disabled={loading || !pagination.hasNext}>
            Next
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="POsgridshad rounded p-0 mt-3">
        {err && <div className="alert alert-danger m-3">{err}</div>}

        <div className="custom-table-container">
          <table className="custom-table">
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => onSort(c.key)}
                    className={c.align === 'right' ? 'text-end' : ''}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {c.label}
                    {sortState.key === c.key ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!loading && visibleRows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center p-4">
                    No data found
                  </td>
                </tr>
              )}

              {!loading &&
                visibleRows.map((r, index) => (
                  <tr key={`${r.branchName}-${index}`}>
                    <td>{r.branchName}</td>
                    <td className="text-end">{r.giftCardCount}</td>
                    <td className="text-end">
                      {Currency?.currency?.code} {numberFmt(r.totalAmount)}
                    </td>
                    <td className="text-end">
                      {Currency?.currency?.code} {numberFmt(r.claimAmount)}
                    </td>
                    <td className="text-end" style={{ color: r.unclaimAmount < 0 ? '#dc3545' : undefined, fontWeight: r.unclaimAmount < 0 ? 700 : 400 }}>
                      {Currency?.currency?.code} {numberFmt(r.unclaimAmount)}
                    </td>
                  </tr>
                ))}

              {loading && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center p-4">
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>

            {sortedRows.length > 0 && (
              <tfoot>
                <tr className="fw-bold bg-light">
                  <td>Total</td>
                  <td className="text-end">{totals.giftCards}</td>
                  <td className="text-end">
                    {Currency?.currency?.code} {numberFmt(totals.total)}
                  </td>
                  <td className="text-end">
                    {Currency?.currency?.code} {numberFmt(totals.claim)}
                  </td>
                  <td className="text-end" style={{ color: totals.unclaim < 0 ? '#dc3545' : undefined }}>
                    {Currency?.currency?.code} {numberFmt(totals.unclaim)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination controls (bottom) */}
        <div className="d-flex justify-content-end align-items-center gap-2 p-3">
          <button className="btn btn-sm btn-outline-secondary" onClick={onPrevPage} disabled={loading || !pagination.hasPrevious}>
            Prev
          </button>
          <span className="text-muted">
            Page <b>{pagination.currentPage}</b> / <b>{pagination.totalPages}</b>
          </span>
          <button className="btn btn-sm btn-outline-secondary" onClick={onNextPage} disabled={loading || !pagination.hasNext}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

// Redux
const mapStateToProps = (state) => state;
export default connect(mapStateToProps)(GiftCardReport);
